<template>
    <app-layout>
        <common-content-spacer height="medium">
            <common-breadcrumbs :breadcrumbs="pageBreadcrumbs"></common-breadcrumbs>
            <common-page-header :heading="pageTitle" size="medium"></common-page-header>
            <common-page-split v-if="!isInsuranceModalOpen" spacing="medium">
                <template #aside>
                    <common-content-group :label="$t('pages.practice.profile.breadcrumbs.practice_profile')">
                        <users-overview
                            :members="practiceMembers"
                            :memberCount="practiceMembersCount"
                            :pendingMembersCount="pendingPracticeMembers"
                        ></users-overview>
                    </common-content-group>
                    <common-content-group
                        v-if="!$auth.isUserPracticeOther()"
                        :label="$t('pages.practice.profile.practice_specialities')"
                    >
                        <p class="users-overview__count">{{ specialities.length }}</p>
                        <common-badges-list
                            v-if="specialities.length"
                            class="users-overview__count-specialities-list"
                            :listElements="specialities"
                        ></common-badges-list>
                    </common-content-group>
                    <common-content-group
                        v-if="!$auth.isUserPracticeOther()"
                        :label="$t('pages.practice.profile.total_referrals_received')"
                    >
                        <p class="users-overview__count">{{ referralsReceivedCount }}</p>
                    </common-content-group>
                    <common-content-group :label="$t('pages.practice.profile.total_referrals_made')">
                        <p class="users-overview__count">{{ referralsCreatedCount }}</p>
                    </common-content-group>
                    <common-content-group :label="$t('pages.practice.profile.practice_locations')">
                        <p class="users-overview__count">{{ locationsCount }}</p>
                    </common-content-group>
                </template>
                <template #main>
                    <common-content-spacer height="large">
                        <profile-photo-upload
                            ref="profilePhotoUpload"
                            :previewPhoto="practiceImage"
                            uploadButtonLabel="forms.profile.account_information.buttons.upload_practice_profile_picture"
                            @save-photo="savePhoto"
                            @remove-photo="removePhoto"
                        ></profile-photo-upload>
                        <practice-information-form
                            @form-changed="formChanged"
                            @is-sending="isSendingData"
                        ></practice-information-form>
                    </common-content-spacer>
                </template>
            </common-page-split>
        </common-content-spacer>
    </app-layout>
</template>

<script>
    import { mapGetters } from 'vuex'
    import AppLayout from 'components/layouts/AppLayout'
    import PageWithDataMixin from 'mixins/PageWithDataMixin'
    import UsersOverview from 'components/page-elements/practice/UsersOverview'
    import PracticeInformationForm from 'components/forms/practice/PracticeInformationForm'
    import UserMembershipStatusEnum from '../../enums/UserMembershipStatusEnum'
    import ProfilePhotoUpload from 'components/page-elements/profile/ProfilePhotoUpload'
    import PracticesAPI from 'api/PracticesAPI'
    import cloneDeep from 'lodash.clonedeep'

    export default {
        mixins: [PageWithDataMixin],
        components: {
            AppLayout,
            UsersOverview,
            PracticeInformationForm,
            ProfilePhotoUpload,
        },

        data() {
            return {
                isInsuranceModalOpen: false,
                insurancePlans: [],
                practiceMembers: [],
                practiceMembersCount: 0,
                pendingPracticeMembers: 0,
                specialities: [],
                referralsReceivedCount: 0,
                referralsCreatedCount: 0,
                locationsCount: 0,
                practiceDataChanged: false,
                isSending: false,
            }
        },
        computed: {
            ...mapGetters({
                practice: 'auth/getPractice',
            }),

            practiceImage() {
                return this.practice?.image?.['thumb-360']
            },

            pageTitle() {
                return this.$t('pages.practice.profile.titles.practice_profile', { practiceName: this.practice.name })
            },

            pageBreadcrumbs() {
                return [
                    {
                        label: this.$t('pages.practice.profile.breadcrumbs.practice'),
                        route: { name: 'practice.profile' },
                    },
                    {
                        label: this.$t('pages.practice.profile.breadcrumbs.practice_profile'),
                    },
                ]
            },
        },

        methods: {
            isSendingData(sending) {
                this.isSending = sending
            },
            formChanged(isChanged) {
                this.practiceDataChanged = isChanged
            },
            openSelectInsurancesModal() {
                this.isInsuranceModalOpen = true
            },
            loadData() {
                const practice = this.$auth.getPractice()
                this.referralsReceivedCount = practice.referralsReceivedCount
                this.referralsCreatedCount = practice.referralsCreatedCount
                this.locationsCount = practice.locations ? 1 + practice.locations.length : 1
                this.specialities = practice.specialities.map((speciality) => speciality.name)
                const members = cloneDeep(this.$auth.getMembers())
                members.forEach((member) => {
                    if (member.practiceMembershipStatus === UserMembershipStatusEnum.PENDING) {
                        this.pendingPracticeMembers += 1
                    }
                })
                this.practiceMembersCount = members.length
                this.practiceMembers = members.splice(0, 3)
            },
            confirmLeave() {
                return confirm(this.$t('pages.practice.profile.confirm_leave'))
            },

            confirmStayInForm() {
                return this.practiceDataChanged && !this.isSending && !this.confirmLeave()
            },

            beforeWindowUnload(e) {
                if (this.confirmStayInForm()) {
                    e.preventDefault()
                    e.returnValue = ''
                }
            },

            savePhoto(file) {
                return PracticesAPI.uploadPhoto(file).then(() => {
                    this.$refs.profilePhotoUpload.resetPhotoUploadModal()
                })
            },

            removePhoto() {
                return PracticesAPI.removePhoto().then(() => {
                    this.$refs.profilePhotoUpload.resetPhotoUploadModal(true)
                })
            },
        },

        beforeRouteLeave(to, from, next) {
            if (this.confirmStayInForm()) {
                next(false)
            } else {
                next()
            }
        },

        created() {
            window.addEventListener('beforeunload', this.beforeWindowUnload)
        },

        mounted() {
            this.loadData()
        },

        beforeDestroy() {
            window.removeEventListener('beforeunload', this.beforeWindowUnload)
        },
    }
</script>
